import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Card,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Loader } from "lucide-react";
import { useToastContext } from "../context/ToastContext";
import { NetworkFetch } from "../network/networkFetch";
import { apiRoutes } from "../network/apiRoutes";
import { formatCurrency } from "../tools/formatCurrency";

interface RechargeWizardProps {
  open: boolean;
  onClose: () => void;
  serviceId: string;
  planVariant: any;
  service: any;
}

const steps = [
  "Selecciona el método de pago",
  "Selecciona el proveedor de pago",
  "Confirmar y pagar",
];

export default function RechargeWizard({
  open,
  onClose,
  serviceId,
  planVariant,
  service,
}: RechargeWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState<string>("");
  const [paymentMethods, setPaymentMethods] = useState<any>({});
  const [paymentGateways, setPaymentGateways] = useState<string[]>([]);
  const [availableGatewaysForMethod, setAvailableGatewaysForMethod] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const [isGeneratingPayment, setIsGeneratingPayment] = useState(false);
  const { success, error: showError } = useToastContext();

  const cooldown = 20; // segundos
  // Verificar cooldown al abrir el wizard
  useEffect(() => {
    if (open) {
      checkAndUpdateCooldown();
    }
  }, [open]);

  // Timer para actualizar el cooldown cada segundo
  useEffect(() => {
    if (cooldownRemaining > 0) {
      const timer = setTimeout(() => {
        const storedTime = localStorage.getItem(`payment-link-cooldown-${serviceId}`);
        if (storedTime) {
          const elapsedSeconds = Math.floor(
            (Date.now() - parseInt(storedTime)) / 1000
          );
          const remaining = Math.max(0, cooldown - elapsedSeconds);
          setCooldownRemaining(remaining);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [cooldownRemaining, serviceId]);

  // Función para verificar y actualizar el estado del cooldown
  const checkAndUpdateCooldown = () => {
    try {
      const storedTime = localStorage.getItem(`payment-link-cooldown-${serviceId}`);
      if (storedTime) {
        const elapsedSeconds = Math.floor(
          (Date.now() - parseInt(storedTime)) / 1000
        );
        const remaining = Math.max(0, cooldown - elapsedSeconds);
        setCooldownRemaining(remaining);
      } else {
        setCooldownRemaining(0);
      }
    } catch (error) {
      // localStorage puede fallar en modo privado de Safari iOS
      console.warn("localStorage no disponible:", error);
      setCooldownRemaining(0);
    }
  };

  // Función para validar si se puede generar un nuevo enlace
  const canGeneratePaymentLink = (): boolean => {
    checkAndUpdateCooldown();
    return cooldownRemaining === 0;
  };

  // Función para establecer el cooldown
  const setCooldown = () => {
    try {
      const now = Date.now();
      localStorage.setItem(`payment-link-cooldown-${serviceId}`, now.toString());
      setCooldownRemaining(cooldown);
    } catch (error) {
      // localStorage puede fallar en modo privado de Safari iOS
      console.warn("No se pudo guardar cooldown:", error);
      setCooldownRemaining(cooldown);
    }
  };

  // Cargar métodos de pago y gateways disponibles
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const data = await NetworkFetch(apiRoutes.getPaymentWays);
        if (data && data.payments) {
          const payments = data.payments;

          const methods: any = {};
          const gateways: any[] = [];

          // Extraer gateways y métodos de pago
          Object.keys(payments).forEach((gatewayKey) => {
            const gateway = payments[gatewayKey];

            if (gateway.status !== true) return;

            gateways.push(gateway.name);

            // Agrupar métodos por gateway
            Object.keys(gateway.methods).forEach((methodKey) => {
              const item: any = gateway.methods[methodKey]
              if (item && item.status == true) {
                if (!methods[methodKey]) {
                  methods[methodKey] = { name: item.name, gateways: [] };
                }
                methods[methodKey].gateways.push(gateway.name);
              }
            });
          });

          setPaymentMethods(methods);
          setPaymentGateways(gateways);
        }
      } catch (err) {
        console.error("Error fetching payment methods:", err);
        setError("Error al cargar los métodos de pago");
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchPaymentMethods();
    }
  }, [open]);

  // Actualizar gateways disponibles cuando se selecciona un método de pago
  useEffect(() => {
    if (selectedPaymentMethod && paymentMethods[selectedPaymentMethod]) {
      const gateways = paymentMethods[selectedPaymentMethod].gateways;
      setAvailableGatewaysForMethod(gateways);

      // Si solo hay 1 gateway, auto-seleccionarlo
      if (gateways.length === 1) {
        setSelectedPaymentGateway(gateways[0]);
      } else {
        setSelectedPaymentGateway("");
      }
    } else {
      setAvailableGatewaysForMethod([]);
      setSelectedPaymentGateway("");
    }
  }, [selectedPaymentMethod, paymentMethods]);



  const handleNext = async () => {
    if (activeStep === 0 && !selectedPaymentMethod) {
      setError("Selecciona un método de pago");
      return;
    }
    if (activeStep === 1 && !selectedPaymentGateway) {
      setError("Selecciona un proveedor de pago");
      return;
    }

    if (activeStep === 2) {
      await generatePaymentLink();
      return;
    }

    setError(null);

    // Si estamos en paso 0 y hay solo 1 gateway, saltamos el paso 1
    if (activeStep === 0 && availableGatewaysForMethod.length === 1) {
      setActiveStep(2);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    // Si venimos del paso 2 por tener solo 1 gateway, regresamos al paso 0
    if (activeStep === 2 && availableGatewaysForMethod.length === 1) {
      setActiveStep(0);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const generatePaymentLink = async () => {
    // Prevenir generación múltiple simultánea
    if (isGeneratingPayment) {
      setError("Ya se está generando un enlace. Por favor, espera a que termine.");
      return;
    }

    // Validar si el enlace ya fue generado recientemente
    if (!canGeneratePaymentLink()) {
      setError(
        `Por favor espera ${cooldownRemaining} segundo${cooldownRemaining !== 1 ? "s" : ""
        } antes de generar otro enlace de pago.`
      );
      showError(
        `Por favor espera ${cooldownRemaining} segundo${cooldownRemaining !== 1 ? "s" : ""
        } antes de generar otro enlace.`
      );
      return;
    }

    // Establecer la bandera de generación en progreso
    setIsGeneratingPayment(true);

    // Establecer el cooldown inmediatamente
    setCooldown();

    // Abrir ventana vacía INMEDIATAMENTE (antes de la llamada async) para evitar bloqueadores
    // En iOS, puede fallar en modo privado o con bloqueadores
    const newWindow = window.open("", "_blank");
    if (!newWindow || typeof newWindow === 'undefined') {
      showError("No se pudo abrir la página de pago. Por favor, verifica tu bloqueador de ventanas emergentes o intenta en modo normal.");
      setIsGeneratingPayment(false);
      return;
    }

    // Mostrar mensaje de carga en la ventana inmediatamente
    const loadingHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Procesando pago...</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
              min-height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .container {
              text-align: center;
              background: white;
              padding: 40px;
              border-radius: 16px;
              box-shadow: 0 20px 50px rgba(59, 130, 246, 0.15);
              max-width: 400px;
            }
            .spinner-wrapper {
              position: relative;
              width: 60px;
              height: 60px;
              margin: 0 auto 25px;
            }
            .spinner {
              width: 60px;
              height: 60px;
              border: 4px solid #f0f4f8;
              border-top: 4px solid #3b82f6;
              border-right: 4px solid #4f46e5;
              border-radius: 50%;
              animation: spin 1.5s linear infinite;
              /* Optimizar para dispositivos móviles */
              will-change: transform;
              -webkit-font-smoothing: antialiased;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            /* Reducir animación en dispositivos de bajo rendimiento */
            @media (prefers-reduced-motion: reduce) {
              .spinner {
                animation: none;
                border-top-color: #3b82f6;
              }
            }
            h2 {
              color: #1e293b;
              margin-bottom: 12px;
              font-size: 22px;
              font-weight: 600;
            }
            p {
              color: #64748b;
              font-size: 14px;
              line-height: 1.6;
              margin-bottom: 8px;
            }
            .info {
              margin-top: 24px;
              padding-top: 20px;
              border-top: 1px solid #e2e8f0;
              color: #94a3b8;
              font-size: 12px;
              font-weight: 500;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="spinner-wrapper">
              <div class="spinner"></div>
            </div>
            <h2>Procesando tu pago...</h2>
            <p>Estamos generando tu enlace de pago seguro.</p>
            <p>Por favor, espera un momento.</p>
            <div class="info">No cierres esta ventana</div>
          </div>
        </body>
      </html>
    `;

    try {
      // document.write() puede fallar en iOS Safari en algunos casos
      // Usar try-catch y fallback para mejorar compatibilidad
      if (newWindow.document && newWindow.document.write) {
        newWindow.document.write(loadingHTML);
        newWindow.document.close();
      } else {
        // Fallback: usar innerHTML si document.write falla
        newWindow.document.body.innerHTML = loadingHTML;
      }
      setLoading(true);

      let paymentURL = null;

      // Check if this is an installation payment
      if (planVariant.isInstallation) {
        const data = await NetworkFetch(apiRoutes.generateInstalationLink, {
          serviceId,
          paymentWay: selectedPaymentGateway,
        });
        paymentURL = data?.link;
      } else {
        const data = await NetworkFetch(apiRoutes.generatePaymentLink, {
          serviceId,
          planId: service.planInfo?.planId || service.plan[0]?.planId,
          variantId: planVariant._id,
          paymentWay: selectedPaymentGateway,
        });
        paymentURL = data?.paymentURL;
      }

      success("Enlace de pago generado");

      // Redirigir a la URL de pago
      if (paymentURL) {
        newWindow.location.href = paymentURL;
        // Cerrar el wizard después de redirigir
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        newWindow.close();
        showError("No se generó el enlace de pago. Por favor, intenta nuevamente.");
      }
    } catch (err) {
      console.error("Error generating payment link:", err);

      // Mostrar error en la ventana abierta
      const errorHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Error en el pago</title>
            <meta charset="UTF-8">
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
                min-height: 100vh;
                display: flex;
                justify-content: center;
                align-items: center;
              }
              .container {
                text-align: center;
                background: white;
                padding: 40px;
                border-radius: 16px;
                box-shadow: 0 20px 50px rgba(239, 68, 68, 0.15);
                max-width: 400px;
              }
              .icon {
                font-size: 52px;
                margin-bottom: 20px;
                line-height: 1;
              }
              h2 {
                color: #dc2626;
                margin-bottom: 12px;
                font-size: 22px;
                font-weight: 600;
              }
              p {
                color: #64748b;
                font-size: 14px;
                line-height: 1.6;
                margin-bottom: 10px;
              }
              button {
                background: linear-gradient(135deg, #3b82f6 0%, #4f46e5 100%);
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 8px;
                cursor: pointer;
                font-size: 14px;
                font-weight: 600;
                margin-top: 20px;
                transition: all 0.3s ease;
                box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
              }
              button:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
              }
              button:active {
                transform: translateY(0);
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="icon">⚠️</div>
              <h2>Error al generar el enlace</h2>
              <p>${err instanceof Error ? err.message : "Ocurrió un error inesperado"}</p>
              <p>Por favor, cierra esta ventana e intenta nuevamente.</p>
              <button onclick="window.close()">Cerrar</button>
            </div>
          </body>
        </html>
      `;

      // Usar try-catch para document.write en iOS
      try {
        if (newWindow.document && newWindow.document.write) {
          newWindow.document.write(errorHTML);
          newWindow.document.close();
        } else {
          newWindow.document.body.innerHTML = errorHTML;
        }
      } catch (writeError) {
        console.warn("Error escribiendo HTML de error en ventana:", writeError);
        newWindow.document.body.innerHTML = `<div style="padding: 20px; font-family: system-ui; text-align: center;"><h2>Error</h2><p>${err instanceof Error ? err.message : 'Error desconocido'}</p><button onclick="window.close()">Cerrar</button></div>`;
      }

      showError(
        err instanceof Error ? err.message : "Error al generar el enlace"
      );
      setError(
        err instanceof Error
          ? err.message
          : "Error al generar el enlace de pago"
      );
    } finally {
      setLoading(false);
      setIsGeneratingPayment(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedPaymentMethod("");
    setSelectedPaymentGateway("");
    setError(null);
    setCooldownRemaining(0);
    setIsGeneratingPayment(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "90vw" },
          maxWidth: { xs: "100vw", sm: "500px" },
          margin: { xs: 0, sm: "auto" },
          borderRadius: { xs: 0, sm: "12px" },
          backgroundColor: "white",
          height: { xs: "100vh", sm: "auto" },
          maxHeight: { xs: "100vh", sm: "90vh" },
        },
      }}
    >
      <DialogTitle sx={{
        fontWeight: 700,
        color: "hsl(240 10% 3.9%)",
        fontSize: { xs: "1.25rem", sm: "1.5rem" },
        p: { xs: 2, sm: 2 }
      }}>
        {planVariant.isInstallation ? "Pago de Instalación" : "Recarga de Crédito"}
      </DialogTitle>

      <DialogContent sx={{
        pt: { xs: 2, sm: 3 },
        px: { xs: 0, sm: 3 }
      }}>
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Stepper
            activeStep={activeStep}
            sx={{
              "& .MuiStepLabel-label": {
                fontSize: { xs: "0.65rem", sm: "0.875rem" },
              },
              "& .MuiStepConnector-root": {
                display: { xs: "none", sm: "block" },
              }
            }}
          >
            {steps.map((label, index) => (
              <Step key={index}>
                <StepLabel sx={{
                  "& .MuiStepIcon-root": {
                    fontSize: { xs: "1.5rem", sm: "2rem" }
                  }
                }}>
                  <span style={{ display: { xs: "none", sm: "inline" } }}>
                    {label}
                  </span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Paso 1: Seleccionar método de pago */}
        {activeStep === 0 && (
          <Box sx={{ px: { xs: 2, sm: 0 } }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "hsl(240 10% 3.9%)",
                mb: 2,
                fontSize: { xs: "0.95rem", sm: "1rem" }
              }}
            >
              Selecciona cómo deseas pagar
            </Typography>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: { xs: 2, sm: 3 },
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : (
              <FormControl fullWidth>
                <RadioGroup
                  value={selectedPaymentMethod}
                  onChange={(e) => {
                    setSelectedPaymentMethod(e.target.value);
                    setError(null);
                  }}
                >
                  {Object.keys(paymentMethods).length > 0 ? (
                    Object.keys(paymentMethods).map((methodKey) => (
                      <Card
                        key={methodKey}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: { xs: 1.5, sm: 2 },
                          border:
                            selectedPaymentMethod === methodKey
                              ? "2px solid hsl(224.3 76.3% 48%)"
                              : "1px solid hsl(240 5.9% 90%)",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "hsl(224.3 76.3% 48%)",
                            backgroundColor: "hsl(224.3 76.3% 95%)",
                          },
                          backgroundColor:
                            selectedPaymentMethod === methodKey
                              ? "hsl(224.3 76.3% 96%)"
                              : "white",
                        }}
                      >
                        <FormControlLabel
                          value={methodKey}
                          control={<Radio />}
                          label={
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: "0.9rem", sm: "1rem" }
                              }}
                            >
                              {paymentMethods[methodKey].name}
                            </Typography>
                          }
                          sx={{ width: "100%", m: 0 }}
                        />
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: "hsl(240 3.8% 46.1%)" }}>
                      No hay métodos de pago disponibles
                    </Typography>
                  )}
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        )}

        {/* Paso 2: Seleccionar proveedor de pago */}
        {activeStep === 1 && (
          <Box sx={{ px: { xs: 2, sm: 0 } }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "hsl(240 10% 3.9%)",
                mb: 2,
                fontSize: { xs: "0.95rem", sm: "1rem" }
              }}
            >
              Selecciona el proveedor de pago
            </Typography>

            <Typography variant="caption" sx={{
              color: "hsl(240 3.8% 46.1%)",
              display: "block",
              mb: 2,
              fontSize: { xs: "0.8rem", sm: "0.875rem" }
            }}>
              Método seleccionado:{" "}
              <strong>
                {paymentMethods[selectedPaymentMethod]?.name}
              </strong>
            </Typography>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: { xs: 2, sm: 3 },
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : (
              <FormControl fullWidth>
                <RadioGroup
                  value={selectedPaymentGateway}
                  onChange={(e) => {
                    setSelectedPaymentGateway(e.target.value);
                    setError(null);
                  }}
                >
                  {availableGatewaysForMethod.length > 0 ? (
                    availableGatewaysForMethod.map((gateway) => (
                      <Card
                        key={gateway}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: { xs: 1.5, sm: 2 },
                          border:
                            selectedPaymentGateway === gateway
                              ? "2px solid hsl(224.3 76.3% 48%)"
                              : "1px solid hsl(240 5.9% 90%)",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "hsl(224.3 76.3% 48%)",
                            backgroundColor: "hsl(224.3 76.3% 95%)",
                          },
                          backgroundColor:
                            selectedPaymentGateway === gateway
                              ? "hsl(224.3 76.3% 96%)"
                              : "white",
                        }}
                      >
                        <FormControlLabel
                          value={gateway}
                          control={<Radio />}
                          label={
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: "0.9rem", sm: "1rem" }
                              }}
                            >
                              {gateway}
                            </Typography>
                          }
                          sx={{ width: "100%", m: 0 }}
                        />
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: "hsl(240 3.8% 46.1%)" }}>
                      No hay proveedores disponibles para este método de pago
                    </Typography>
                  )}
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        )}

        {/* Paso 3: Confirmar y pagar */}
        {activeStep === 2 && (
          <Box sx={{ px: { xs: 2, sm: 0 } }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "hsl(240 10% 3.9%)",
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: "0.95rem", sm: "1rem" }
              }}
            >
              Confirmar datos
            </Typography>

            <Card sx={{
              p: { xs: 2, sm: 3 },
              backgroundColor: "hsl(240 4.8% 95.9%)",
              mb: { xs: 2, sm: 3 }
            }}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "hsl(240 3.8% 46.1%)",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" }
                  }}
                >
                  Plan
                </Typography>
                <Typography variant="body2" sx={{
                  color: "hsl(240 10% 3.9%)",
                  fontSize: { xs: "0.9rem", sm: "1rem" }
                }}>
                  {planVariant.name}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "hsl(240 3.8% 46.1%)",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" }
                  }}
                >
                  Monto
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "hsl(34.3 97.3% 50.4%)",
                    fontWeight: 700,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" }
                  }}
                >
                  {formatCurrency(planVariant.price)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "hsl(240 3.8% 46.1%)",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" }
                  }}
                >
                  Método de pago
                </Typography>
                <Typography variant="body2" sx={{
                  color: "hsl(240 10% 3.9%)",
                  fontSize: { xs: "0.9rem", sm: "1rem" }
                }}>
                  {paymentMethods[selectedPaymentMethod]?.name}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "hsl(240 3.8% 46.1%)",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" }
                  }}
                >
                  Proveedor de pago
                </Typography>
                <Typography variant="body2" sx={{
                  color: "hsl(240 10% 3.9%)",
                  fontSize: { xs: "0.9rem", sm: "1rem" }
                }}>
                  {selectedPaymentGateway}
                </Typography>
              </Box>
            </Card>

            {cooldownRemaining > 0 && (
              <Alert severity="warning" sx={{
                fontSize: { xs: "0.85rem", sm: "1rem" },
                mb: 2
              }}>
                <strong>Enlace en período de espera:</strong> Por favor espera {cooldownRemaining} segundo{cooldownRemaining !== 1 ? "s" : ""} antes de generar otro enlace de pago.
              </Alert>
            )}

            <Alert severity="info" sx={{
              fontSize: { xs: "0.85rem", sm: "1rem" }
            }}>
              Al continuar, serás redirigido al formulario de pago seguro.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: { xs: 1.5, sm: 2 },
        gap: 1,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        flexDirection: { xs: "column-reverse", sm: "row" }
      }}>
        <Button
          onClick={handleClose}
          sx={{
            color: "hsl(240 3.8% 46.1%)",
            textTransform: "none",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Cancelar
        </Button>

        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            disabled={loading}
            sx={{
              color: "hsl(224.3 76.3% 48%)",
              textTransform: "none",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Atrás
          </Button>
        )}

        <Button
          onClick={handleNext}
          disabled={
            loading ||
            isGeneratingPayment ||
            (activeStep === 0 && !selectedPaymentMethod) ||
            (activeStep === 1 && !selectedPaymentGateway) ||
            (activeStep === 2 && cooldownRemaining > 0)
          }
          variant="contained"
          sx={{
            backgroundColor: "hsl(34.3 97.3% 50.4%)",
            color: "white",
            textTransform: "none",
            fontWeight: 600,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {loading || isGeneratingPayment ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
              Procesando...
            </Box>
          ) : activeStep === 2 && cooldownRemaining > 0 ? (
            `Espera ${cooldownRemaining}s`
          ) : planVariant.isInstallation || activeStep === 2 ? (
            "Continuar al Pago"
          ) : (
            "Siguiente"
          )}
        </Button>
      </DialogActions>

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </Dialog>
  );
}
